import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Resizable, ResizableBox } from 'react-resizable';
import classnames from 'classnames';

import styles from './ResizePanel.module.scss';

function getParsedSize(size, defaultSize) {
  const parsedSize = typeof size === 'number' ? size : parseInt(size, 10);
  return Number.isNaN(parsedSize) ? defaultSize : parsedSize;
}

const sideToHandle = {
  top: 'n',
  right: 'e',
  bottom: 's',
  left: 'w',
};

const getCallbackSize = (dimensions, isVertical) =>
  Math.round(dimensions.size[isVertical ? 'height' : 'width']);

// eslint-disable-next-line react/display-name
export const makeReactResizableHandleGetter = opts => (axis, ref) => (
  <div
    ref={ref}
    className={classnames(styles.handle, styles[axis], opts?.classes?.handle)}
  >
    <div
      className={classnames(
        styles.handleHighlight,
        opts?.classes?.handleHighlight,
      )}
    />
  </div>
);

export default function ResizePanel({
  children,
  classes,
  className,
  defaultSize,
  disabled,
  handleDOMSizing,
  minSize,
  maxSize,
  onResize: onResizeProp,
  onResizeStop: onResizeStopProp,
  side,
  size,
}) {
  const parsedSize = getParsedSize(size, defaultSize);
  const isVertical = side === 'top' || side === 'bottom';

  const sizeProps = useMemo(() => {
    return isVertical
      ? { axis: 'y', height: parsedSize }
      : { axis: 'x', width: parsedSize };
  }, [isVertical, parsedSize]);

  const onResize = useCallback(
    (e, dimensions) =>
      onResizeProp && onResizeProp?.(getCallbackSize(dimensions, isVertical)),
    [onResizeProp, isVertical],
  );
  const onResizeStop = useCallback(
    (e, dimensions) =>
      onResizeStopProp &&
      onResizeStopProp(getCallbackSize(dimensions, isVertical)),
    [onResizeStopProp, isVertical],
  );
  const Component = handleDOMSizing ? ResizableBox : Resizable;

  return (
    <Component
      // Placate Resizable propTypes...
      height={-1}
      width={-1}
      {...(disabled ? {} : sizeProps)}
      className={classnames(styles.resizePanel, styles[side], className)}
      handle={makeReactResizableHandleGetter({ classes })}
      minConstraints={useMemo(
        () => (isVertical ? [Infinity, minSize] : [minSize, Infinity]),
        [isVertical, minSize],
      )}
      maxConstraints={useMemo(
        () => (isVertical ? [Infinity, maxSize] : [maxSize, Infinity]),
        [isVertical, maxSize],
      )}
      resizeHandles={useMemo(
        () => (disabled ? [] : [sideToHandle[side] ?? sideToHandle.top]),
        [disabled, side],
      )}
      onResize={onResize}
      onResizeStop={onResizeStop}
    >
      {children}
    </Component>
  );
}
ResizePanel.displayName = 'ResizePanel';
ResizePanel.propTypes = {
  children: PropTypes.node.isRequired,
  classes: PropTypes.shape({
    handle: PropTypes.string,
  }),
  className: PropTypes.string,
  defaultSize: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  /**
   * handleDOMSizing tells the component to actually use the size on an added child div. When this
   * prop is not used the consumer is expected to handle sizing manually via onResize prop callback.
   */
  handleDOMSizing: PropTypes.bool,
  minSize: PropTypes.number.isRequired,
  maxSize: PropTypes.number.isRequired,
  onResize: PropTypes.func,
  onResizeStop: PropTypes.func,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  side: PropTypes.oneOf(['top', 'right', 'bottom', 'left']).isRequired,
};
ResizePanel.defaultProps = {
  classes: {},
  className: null,
  disabled: false,
  handleDOMSizing: false,
  onResize: null,
  onResizeStop: null,
};

export function HorizontalResizePanel({ ...props }) {
  return <ResizePanel {...props} />;
}
HorizontalResizePanel.displayName = 'HorizontalResizePanel';
HorizontalResizePanel.propTypes = {
  ...ResizePanel.propTypes,
  side: PropTypes.oneOf(['right', 'left']),
};
HorizontalResizePanel.defaultProps = {
  ...ResizePanel.defaultProps,
  side: 'right',
};

export function VerticalResizePanel({ ...props }) {
  return <ResizePanel {...props} />;
}
VerticalResizePanel.displayName = 'VerticalResizePanel';
VerticalResizePanel.propTypes = {
  ...ResizePanel.propTypes,
  side: PropTypes.oneOf(['top', 'bottom']).isRequired,
};
VerticalResizePanel.defaultProps = {
  ...ResizePanel.defaultProps,
  side: 'top',
};
